// Simple roles helper and hook for reading user role from Firestore
import { useEffect, useState } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../../firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';

export const ROLES = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  SEN_CO: 'sen_co',
  TEACHER: 'teacher',
  TA: 'ta',
  // Solo user (individual subscriber, not linked to school)
  SOLO_USER: 'solo_user',
  // Legacy support (90-day compatibility)
  SENCO: 'SENCO',
  TEACHER_LEGACY: 'TEACHER',
  TA_LEGACY: 'TA',
};

/**
 * Human-readable display labels for school access tiers.
 * Keys match the legacy ROLES values used in Firestore (SENCO, TEACHER, TA)
 * as well as the normalised values (sen_co, teacher, ta).
 * Use these everywhere a role is shown to a user.
 */
export const ROLE_LABELS = {
  // Normalised values
  sen_co:  'Full Access (Senior Leader / Head Teacher / SENCO)',
  teacher: 'Standard Access (Teacher)',
  ta:      'Read-Only (Teaching Assistant)',
  // Legacy uppercase values stored in Firestore
  SENCO:   'Full Access (Senior Leader / Head Teacher / SENCO)',
  TEACHER: 'Standard Access (Teacher)',
  TA:      'Read-Only (Teaching Assistant)',
  // Admin tiers
  super_admin: 'Super Admin',
  admin:       'Admin',
};

/**
 * Returns the display label for a given role value, falling back to the raw value.
 */
export function getRoleLabel(role) {
  return ROLE_LABELS[role] ?? role ?? 'Unknown';
}

// User types - determines which features are available
export const USER_TYPES = {
  SCHOOL: 'school',  // Linked to a school, has schoolId
  SOLO: 'solo',      // Individual subscriber, no schoolId
};

// Subscription statuses for solo users
export const SUBSCRIPTION_STATUS = {
  NONE: 'none',
  TRIALING: 'trialing',
  ACTIVE: 'active',
  PAST_DUE: 'past_due',
  CANCELLED: 'cancelled',
};

export function useUserRole(options = { subscribe: true }) {
  const [user] = useAuthState(auth);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      setRole(null);
      setLoading(false);
      return;
    }

    const userDocRef = doc(db, 'users', user.uid);

    if (options.subscribe) {
      const unsub = onSnapshot(
        userDocRef,
        (snap) => {
          setRole(snap.exists() ? snap.data().role || null : null);
          setLoading(false);
        },
        (err) => {
          setError(err);
          setLoading(false);
        }
      );
      return () => unsub();
    } else {
      (async () => {
        try {
          const snap = await getDoc(userDocRef);
          setRole(snap.exists() ? snap.data().role || null : null);
        } catch (e) {
          setError(e);
        } finally {
          setLoading(false);
        }
      })();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return { role, loading, error };
}

/**
 * Map legacy and normalised Firestore role values to a single canonical form.
 */
export function normalizeRole(rawRole) {
  if (!rawRole) return null;
  if (rawRole === ROLES.SENCO || rawRole === ROLES.SEN_CO) return ROLES.SEN_CO;
  if (rawRole === ROLES.TEACHER || rawRole === ROLES.TEACHER_LEGACY) return ROLES.TEACHER;
  if (rawRole === ROLES.TA || rawRole === ROLES.TA_LEGACY) return ROLES.TA;
  return rawRole;
}

export function isSencoRole(role) {
  return normalizeRole(role) === ROLES.SEN_CO;
}

export function isTeacherRole(role) {
  return normalizeRole(role) === ROLES.TEACHER;
}

export function isTaRole(role) {
  return normalizeRole(role) === ROLES.TA;
}

export function hasRole(role, allowed) {
  if (!role || !allowed) return false;
  const userRole = normalizeRole(role) ?? role;
  const allowedList = Array.isArray(allowed) ? allowed : [allowed];
  return allowedList.some((entry) => {
    const allowedRole = normalizeRole(entry) ?? entry;
    return userRole === allowedRole || role === entry;
  });
}

/**
 * Hook to get the current user's type (school or solo)
 */
export function useUserType() {
  const [user] = useAuthState(auth);
  const [userType, setUserType] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user) {
      setUserType(null);
      setSubscriptionStatus(null);
      setLoading(false);
      return;
    }

    const userDocRef = doc(db, 'users', user.uid);

    const unsub = onSnapshot(
      userDocRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setUserType(data.userType || USER_TYPES.SCHOOL);
          setSubscriptionStatus(data.subscriptionStatus || SUBSCRIPTION_STATUS.NONE);
        } else {
          setUserType(USER_TYPES.SCHOOL);
          setSubscriptionStatus(SUBSCRIPTION_STATUS.NONE);
        }
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [user]);

  return { userType, subscriptionStatus, loading, error };
}

/**
 * Check if user is a solo user with active subscription
 */
export function isSoloUserActive(userType, subscriptionStatus) {
  return userType === USER_TYPES.SOLO && 
    (subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE || 
     subscriptionStatus === SUBSCRIPTION_STATUS.TRIALING);
}

